/**
 * Users view — Phase 2.3.c.
 *
 * Owns the Users tab: list of admin users, role badges, add/edit user
 * dialog, manage-roles dialog (with role-display ordering and help-text
 * hints), and pagination + search.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `openDialog` — generic.
 *   - `getRoleDisplayOrder`, `getRoleLabels`, `getRoleHelpText` — admin.js
 *     owns the canonical role-metadata constants.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports from `src/lib/format.js`, `src/lib/dialog.js`, and
 * `src/lib/roles.js`. The `addUserBtn` topbar button is resolved
 * locally via `getElementById`.
 */

import { escapeHtml } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import {
  ROLE_DISPLAY_ORDER as ROLES_DISPLAY_ORDER,
  ROLE_LABELS as ROLES_LABELS,
  ROLE_HELP_TEXT as ROLES_HELP_TEXT,
} from '../../src/lib/roles.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const adminAPI = api;
const API = api.routes;
const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

const getRoleDisplayOrder = () => ROLES_DISPLAY_ORDER;
const getRoleLabels = () => ROLES_LABELS;
const getRoleHelpText = () => ROLES_HELP_TEXT;
const getAddUserBtn = () => document.getElementById('addUserBtn');

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let usersTbody, userDlg, userForm, userRolesDlg, userRolesForm, userRolesList,
      usersPrev, usersNext, usersPageInfo, userSearchInput, usersShowInactive;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    usersTbody = document.getElementById('usersTbody');
    userDlg = document.getElementById('userDlg');
    userForm = document.getElementById('userForm');
    userRolesDlg = document.getElementById('userRolesDlg');
    userRolesForm = document.getElementById('userRolesForm');
    userRolesList = document.getElementById('userRolesList');
    usersPrev = document.getElementById('usersPrev');
    usersNext = document.getElementById('usersNext');
    usersPageInfo = document.getElementById('usersPageInfo');
    userSearchInput = document.getElementById('userSearch');
    usersShowInactive = document.getElementById('usersShowInactive');
  }

  // ---- State ----------------------------------------------------------
  let usersOffset = 0;
  let usersLimit = 50;
  let usersSearchQuery = '';

  function renderUsers(items = []) {
    usersTbody.innerHTML = '';
    if (!items.length) { usersTbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">No users</td></tr>'; usersPageInfo.textContent = `${Math.floor(usersOffset/usersLimit)+1}`; return; }
    // Roles come straight from the list response so a fresh reload always reflects
    // the latest assignment (no per-row cache to go stale after a role change).
    usersTbody.innerHTML = items.map((u, idx) => {
      const roles = Array.isArray(u.roles) ? u.roles : [];
      return `<tr>
        <td>${idx + 1 + usersOffset}</td>
        <td>${escapeHtml(u.full_name || '')}</td>
        <td>${escapeHtml(u.username || '')}</td>
        <td>${escapeHtml(u.email || '')}</td>
        <td>${escapeHtml(roles.join(', '))}</td>
        <td>${escapeHtml(u.is_active ? 'Enabled' : 'Disabled')}</td>
        <td>
          <button class="btn" data-uact="edit" data-id="${u.id}">✏️ Edit</button>
          <button class="btn danger" data-uact="delete" data-id="${u.id}">🗑 Remove</button>
          <button class="btn" data-uact="roles" data-id="${u.id}">🔑 Roles</button>
        </td></tr>`;
    }).join('');
    usersPageInfo.textContent = `${Math.floor(usersOffset/usersLimit)+1}`;
  }

  async function openUserRolesDialog(id) {
    userRolesForm.reset();
    userRolesForm.id.value = id;
    try {
      const [allRoles, userRoles] = await Promise.all([
        adminAPI.fetch(API.roles, { method: 'GET' }),
        adminAPI.fetch(API.userRoles(id), { method: 'GET' }),
      ]);
      const have = new Set(Array.isArray(userRoles) ? userRoles : []);
      const ROLE_DISPLAY_ORDER = getRoleDisplayOrder();
      const ROLE_LABELS = getRoleLabels();
      const ROLE_HELP_TEXT = getRoleHelpText();
      userRolesList.innerHTML = '';
      const combinedRoles = Array.from(new Set([
        ...(Array.isArray(allRoles) ? allRoles : []),
        ...ROLE_DISPLAY_ORDER,
      ])).filter(Boolean);
      combinedRoles
        .sort((a, b) => {
          const ia = ROLE_DISPLAY_ORDER.indexOf(a);
          const ib = ROLE_DISPLAY_ORDER.indexOf(b);
          const normA = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
          const normB = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
          if (normA !== normB) return normA - normB;
          return String(a).localeCompare(String(b));
        })
        .forEach((roleName) => {
          const idAttr = `role_${roleName}`;
          const label = ROLE_LABELS[roleName] || roleName.replace(/_/g, ' ');
          const hint = ROLE_HELP_TEXT[roleName] || '';
          const wrap = document.createElement('div');
          wrap.className = 'field';
          wrap.innerHTML = `
            <label style="display:flex; gap:10px; align-items:flex-start;">
              <input type="checkbox" name="role_${roleName}" id="${idAttr}" ${have.has(roleName) ? 'checked' : ''} />
              <span style="display:flex; flex-direction:column; gap:2px;">
                <strong>${escapeHtml(label)}</strong>
                ${hint ? `<small style="color:var(--sub);">${escapeHtml(hint)}</small>` : ''}
              </span>
            </label>`;
          userRolesList.appendChild(wrap);
        });
      openDialog(userRolesDlg);
    } catch (err) { showFlash(err.message, 'error'); }
  }

  async function openUserDialog(id = null) {
    userForm.reset();
    userForm.id.value = id || '';
    document.getElementById('userDlgTitle').textContent = id ? 'Edit user' : 'Add user';
    if (id) {
      try {
        const u = await adminAPI.fetch(API.user(id), { method: 'GET' });
        userForm.username.value = u.username || '';
        userForm.full_name.value = u.full_name || '';
        userForm.email.value = u.email || '';
        userForm.is_active.value = u.is_active;
      } catch (err) { showFlash(err.message, 'error'); return; }
    }
    openDialog(userDlg);
  }

  async function loadUsers() {
    try {
      const p = new URLSearchParams();
      if (usersSearchQuery) p.set('search', usersSearchQuery);
      // Default: active accounts only; the toggle includes disabled ones.
      if (usersShowInactive && usersShowInactive.checked) p.set('active_only', 'false');
      p.set('skip', String(usersOffset));
      p.set('limit', String(usersLimit));
      const data = await adminAPI.fetch(`${API.users}?${p.toString()}`, { method: 'GET' });
      const items = Array.isArray(data) ? data : (data.items || []);
      items.sort((a, b) => {
        const aName = (a.full_name || a.username || '').toLowerCase();
        const bName = (b.full_name || b.username || '').toLowerCase();
        return aName.localeCompare(bName);
      });
      renderUsers(items);
    }
    catch (err) { renderUsers([]); showFlash(err.message, 'error'); }
  }

  let mounted = false;
  function mount() {
    if (mounted) return;
    mounted = true;
    resolveDom();
    const addUserBtn = getAddUserBtn();
    if (addUserBtn) addUserBtn.addEventListener('click', () => openUserDialog());
    if (userSearchInput) {
      userSearchInput.addEventListener('input', (e) => { usersSearchQuery = (e.target.value || '').trim(); usersOffset = 0; loadUsers(); });
    }
    if (usersShowInactive) {
      usersShowInactive.addEventListener('change', () => { usersOffset = 0; loadUsers(); });
    }
    if (usersTbody) {
      usersTbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-uact]');
        if (!btn) return;
        const id = btn.dataset.id;
        const act = btn.dataset.uact;
        if (act === 'edit') return openUserDialog(id);
        if (act === 'roles') return openUserRolesDialog(id);
        if (act === 'delete') {
          if (!confirm('Remove this user?')) return;
          try { await adminAPI.fetch(API.user(id), { method: 'DELETE' }); showFlash('User removed'); loadUsers(); }
          catch (err) { showFlash(err.message, 'error'); }
        }
      });
    }
    if (userRolesForm) {
      userRolesForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const id = userRolesForm.id.value.trim();
        if (!id) return userRolesDlg.close();
        try {
          const [allRoles, currentRoles] = await Promise.all([
            adminAPI.fetch(API.roles, { method: 'GET' }),
            adminAPI.fetch(API.userRoles(id), { method: 'GET' }),
          ]);
          const sel = new Set();
          const ROLE_DISPLAY_ORDER = getRoleDisplayOrder();
          const allSelectable = Array.from(new Set([
            ...(Array.isArray(allRoles) ? allRoles : []),
            ...ROLE_DISPLAY_ORDER,
          ])).filter(Boolean);
          allSelectable.forEach(r => {
            const cb = userRolesForm.querySelector(`input[name="role_${r}"]`);
            if (cb && cb.checked) sel.add(r);
          });
          const cur = new Set(Array.isArray(currentRoles) ? currentRoles : []);
          for (const r of sel) {
            if (!cur.has(r)) await adminAPI.fetch(API.userAssignRole(id), { method: 'POST', body: JSON.stringify({ name: r }) });
          }
          for (const r of cur) {
            if (!sel.has(r)) await adminAPI.fetch(API.userRevokeRole(id, r), { method: 'DELETE' });
          }
          userRolesDlg.close();
          showFlash('Roles updated');
          loadUsers();
        } catch (err) { showFlash(err.message, 'error'); }
      });
    }
    if (userForm) {
      userForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const id = userForm.id.value.trim();
        const payload = {
          username: userForm.username.value.trim(),
          full_name: userForm.full_name.value.trim(),
          email: userForm.email.value.trim(),
          is_active: userForm.is_active.value,
        };
        if (userForm.password.value && userForm.password.value.trim().length > 0) {
          payload.password = userForm.password.value;
        }
        try {
          if (id) await adminAPI.fetch(API.user(id), { method: 'PUT', body: JSON.stringify(payload) });
          else await adminAPI.fetch(API.users, { method: 'POST', body: JSON.stringify(payload) });
          userDlg.close();
          showFlash('User saved');
          loadUsers();
        } catch (err) { showFlash(err.message, 'error'); }
      });
    }
    if (usersPrev && usersNext) {
      usersPrev.addEventListener('click', () => { usersOffset = Math.max(0, usersOffset - usersLimit); loadUsers(); });
      usersNext.addEventListener('click', () => { usersOffset += usersLimit; loadUsers(); });
    }
  }

export default {
  mount,
  init: () => { resolveDom(); loadUsers(); },
};
